import { FallbackErrorBoundary } from "@/components/FallbackErrorBoundary";
import {
  ProjectSecondLevelHeader,
  WORKSPACE_TAB,
} from "@/features/components/ProjectSecondLevelHeader";
import { SearchProjectProvider } from "@/features/projects/SearchProject";
import { useAiFeatureConfig } from "@/features/projects/hooks/useAiFeatureConfig";
import { WaitingListWidget } from "@/features/workspace/widgets/waitingList/WaitingListWidget";
import { useShowSubHeader } from "@/hooks/useSubHeaderVisibility";
import { useTitle } from "@/hooks/useTitle";
import styled from "@emotion/styled";
import { useWidgetPreferences } from "@repo/api";
import { Variables } from "@repo/skapa";
import { t } from "@repo/translation";
import { SectionContainer, useSettings } from "@repo/ui";

import {
  type GridWidget,
  calculateWidgetGrid,
  convertFromOldWidgetPosToNew,
} from "./calculateGrid";
import { useTrackWidgetAnalytics } from "./useTrackWidgetAnalytics";
import { TaskWidget } from "./widgets/TaskWidget";
import { AlertWidget } from "./widgets/alert/AlertWidget";
import { AppointmentsWidget } from "./widgets/appointments/AppointmentsWidget";
import { CalendarWidget } from "./widgets/calendar/CalendarWidget";
import { GeneratedProjectsWidget } from "./widgets/generatedProjects/GeneratedProjectsWidget";
import { InstallationQuotationWidget } from "./widgets/installationQuotations/InstallationQuotationWidget";
import { PrecallWidget } from "./widgets/precall/PrecallWidget";
import { ProjectsWidget } from "./widgets/projects/ProjectsWidget";
import { QuotationsWidget } from "./widgets/quotations/QuotationsWidget";
import { WorkOrderWidget } from "./widgets/workOrders/WorkOrderWidget";

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${Variables.space75} 24px;
  gap: 24px;

  @media (min-width: ${Variables.breakpointL}) {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
`;

interface GridCellProps {
  col?: string;
  row?: string;

  height?: string;
}

const GridCell = styled.div<GridCellProps>`
  grid-column: ${(props) => props.col};
  grid-row: ${(props) => props.row};
`;

const renderWidget = (
  widget: GridWidget,
  isQuotationImportEnabled?: boolean,
  isSalesforceIntegrationEnabled?: boolean,
  isGeneratedProjectsEnabled?: boolean,
  isWaitingListEnabled?: boolean
) => {
  let component = null;

  switch (widget.widgetType) {
    case "TASKS":
      component = (
        <TaskWidget
          title={widget.widgetAliasName ?? t("label.tasks")}
          size={widget.widgetSettings.appSize.size}
          settings={widget.widgetSettings.appSetting}
        />
      );
      break;
    case "ALERTS":
      component = (
        <AlertWidget
          title={widget.widgetAliasName ?? t("label.project_notifications")}
          size={widget.widgetSettings.appSize.size}
        />
      );
      break;
    case "CALENDAR":
      component = (
        <CalendarWidget
          title={widget.widgetAliasName ?? t("label.calendar_title")}
          size={widget.widgetSettings.appSize.size}
          settings={widget.widgetSettings.appSetting}
        />
      );
      break;
    case "PROJECTS":
      component = (
        <SearchProjectProvider pageSize={5} refreshResult>
          <ProjectsWidget
            title={widget.widgetAliasName ?? t("label.projects")}
            settings={widget.widgetSettings.appSetting}
            size={widget.widgetSettings.appSize.size}
          />
        </SearchProjectProvider>
      );
      break;
    case "PRECALL":
      component = (
        <PrecallWidget
          title={widget.widgetAliasName ?? t("label.precall")}
          size={widget.widgetSettings.appSize.size}
          settings={widget.widgetSettings.appSetting}
        />
      );
      break;
    case "QUOTATIONS":
      component = isQuotationImportEnabled && (
        <QuotationsWidget
          title={widget.widgetAliasName ?? t("label.quotations")}
          widgetSize={widget.widgetSettings.appSize.size}
          settings={widget.widgetSettings.appSetting}
        />
      );
      break;
    case "APPOINTMENTS":
      component = (
        <AppointmentsWidget
          title={widget.widgetAliasName ?? t("label.appointments")}
          widgetSize={widget.widgetSettings.appSize.size}
          settings={widget.widgetSettings.appSetting}
        />
      );
      break;
    case "INSTALLATION_QUOTATIONS":
      component = isSalesforceIntegrationEnabled && (
        <InstallationQuotationWidget
          title={widget.widgetAliasName ?? t("label.installation_quotations")}
          widgetSize={widget.widgetSettings.appSize.size}
          settings={widget.widgetSettings.appSetting}
        />
      );
      break;
    case "WORK_ORDERS":
      component = isSalesforceIntegrationEnabled && (
        <WorkOrderWidget
          title={widget.widgetAliasName ?? t("label.work_orders")}
          widgetSize={widget.widgetSettings.appSize.size}
          settings={widget.widgetSettings.appSetting}
        />
      );
      break;

    case "FOLLOWUP_LIST":
      component = isGeneratedProjectsEnabled && (
        <SearchProjectProvider pageSize={5} refreshResult>
          <GeneratedProjectsWidget
            title={widget.widgetAliasName ?? t("label.follow_up_list")}
            size={widget.widgetSettings.appSize.size}
            settings={widget.widgetSettings.appSetting}
          />
        </SearchProjectProvider>
      );
      break;

    case "WAITING_LIST":
      component = isWaitingListEnabled && (
        <WaitingListWidget
          title={widget.widgetAliasName ?? t("label.waiting_list")}
          size={widget.widgetSettings.appSize.size}
          settings={widget.widgetSettings.appSetting}
        />
      );
      break;
  }

  return (
    <GridCell
      key={widget.workspacePreferenceId}
      col={widget.gridCol}
      row={widget.gridRow}
    >
      <FallbackErrorBoundary
        errorMessage={`Failed to render widget: ${widget.widgetAliasName} ${widget.workspacePreferenceId} `}
      >
        {component}
      </FallbackErrorBoundary>
    </GridCell>
  );
};

export const Workspace = () => {
  useTitle("Workspace");
  const { data, isLoading } = useWidgetPreferences();
  const convertedWidgets = convertFromOldWidgetPosToNew(data);
  const widgets = calculateWidgetGrid(convertedWidgets ?? data ?? []);
  const { feature_flags, quotation, waiting_list } = useSettings();
  const {
    isSubHeaderVisible,
    isValidating: isSubHeaderValidating,
    isLoading: isSubHeaderLoading,
  } = useShowSubHeader();
  const isGeneratedProjectsEnabled = useAiFeatureConfig({
    feature: "generatedProjects",
  });

  // If it's empty => use hardcoded workspace for now
  const customWorkspace = data !== undefined;

  useTrackWidgetAnalytics(data, customWorkspace);

  return (
    <>
      {!isSubHeaderValidating && !isSubHeaderLoading && !isSubHeaderVisible && (
        <ProjectSecondLevelHeader activeTabPanelId={WORKSPACE_TAB} />
      )}

      <div role="tabpanel" id={`${WORKSPACE_TAB}_TAB`}>
        <SectionContainer>
          {customWorkspace && (
            <Grid>
              {widgets.map((widget) =>
                renderWidget(
                  widget,
                  quotation?.enable_quotations_import,
                  feature_flags?.enable_salesforce_integration,
                  isGeneratedProjectsEnabled,
                  waiting_list?.waiting_list_enabled
                )
              )}
            </Grid>
          )}
          {!isLoading && !customWorkspace && <HardcodedWorkspace />}
        </SectionContainer>
      </div>
    </>
  );
};

const HardcodedWorkspace = () => {
  const isGeneratedProjectsEnabled = useAiFeatureConfig({
    feature: "generatedProjects",
  });
  const layout = isGeneratedProjectsEnabled
    ? {
        generatedProjects: { col: "1 / span 4", row: "1" },
        task: { col: "1", row: "2 / span 2" },
        alert: { col: "2 / span 2", row: "2" },
        calendar: { col: "4", row: "2" },
        projects: { col: "2 / span 3", row: "3" },
        appointments: { col: "1 / span 3", row: "4" },
        quotations: { col: "4", row: "4" },
      }
    : {
        task: { col: "1", row: "1 / span 2" },
        alert: { col: "2 / span 2", row: "1" },
        calendar: { col: "4", row: "1" },
        projects: { col: "2 / span 3", row: "2" },
        appointments: { col: "1 / span 3", row: "3" },
        quotations: { col: "4", row: "3" },
      };

  return (
    <Grid>
      <GridCell col={layout.task.col} row={layout.task.row}>
        <TaskWidget title={t("label.tasks")} size="M" />
      </GridCell>
      <GridCell col={layout.alert.col} row={layout.alert.row}>
        <AlertWidget title={t("label.project_notifications")} size="M" />
      </GridCell>
      <GridCell col={layout.calendar.col} row={layout.calendar.row}>
        <CalendarWidget title={t("label.calendar_title")} size="S" />
      </GridCell>
      <GridCell col={layout.projects.col} row={layout.projects.row}>
        <SearchProjectProvider>
          <ProjectsWidget title={t("label.projects")} size="M" />
        </SearchProjectProvider>
      </GridCell>
      <GridCell col={layout.appointments.col} row={layout.appointments.row}>
        <AppointmentsWidget title={t("label.appointments")} widgetSize="M" />
      </GridCell>
      <GridCell col={layout.quotations.col} row={layout.quotations.row}>
        <QuotationsWidget title={t("label.quotations")} widgetSize="M" />
      </GridCell>
      {isGeneratedProjectsEnabled && (
        <GridCell
          col={layout.generatedProjects!.col}
          row={layout.generatedProjects!.row}
        >
          <SearchProjectProvider>
            <GeneratedProjectsWidget
              title={t("label.follow_up_list")}
              size="L"
            />
          </SearchProjectProvider>
        </GridCell>
      )}
    </Grid>
  );
};
